import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { FAQS } from "@/data/content";
import { pageView } from "@/analytics";

const BASE_URL = "https://www.edilcomplex2020.it";
const OG_IMAGE =
  "https://static.wixstatic.com/media/ea26fd_e142fd7bcd8c46219312b22e8a91bb3c~mv2_d_4000_2447_s_4_2.jpeg/v1/fill/w_1200,h_630,al_c,q_85,enc_avif,quality_auto/ea26fd_e142fd7bcd8c46219312b22e8a91bb3c~mv2_d_4000_2447_s_4_2.jpeg";

const META = {
  "/": {
    name: "Home",
    title: "Impresa Edile Lucca | Edilcomplex2020 — Costruzioni e Ristrutturazioni in Toscana",
    description:
      "Edilcomplex2020 S.r.l., impresa edile a Lucca: costruzioni residenziali, ristrutturazioni chiavi in mano, restauri e piscine di pregio in tutta la Toscana. 30 anni di esperienza. Preventivo gratuito su WhatsApp.",
  },
  "/servizi": {
    name: "Servizi",
    title: "Servizi Edili a Lucca | Costruzioni, Ristrutturazioni, Restauri e Piscine",
    description:
      "Costruzioni di nuovi edifici, ristrutturazioni edilizie, restauri, edifici commerciali, piscine con quarzo naturale e gestione cantieri a Lucca e in Toscana. Soluzioni chiavi in mano.",
  },
  "/progetti": {
    name: "Progetti",
    title: "Progetti Realizzati in Toscana | Edilcomplex2020 Impresa Edile Lucca",
    description:
      "Scopri i progetti dell'impresa edile Edilcomplex2020: casa a Lucca, piscina a Barberino, nuova costruzione a Marina di Pietrasanta. Qualità e finiture di pregio.",
  },
  "/chi-siamo": {
    name: "Chi Siamo",
    title: "Chi Siamo | Edilcomplex2020 — Impresa Edile a Lucca dal 1994",
    description:
      "Trent'anni di esperienza edilizia in Toscana, rinnovati nel 2020 con tecnologie all'avanguardia. Un team di professionisti per costruzioni e ristrutturazioni di pregio.",
  },
  "/contatti": {
    name: "Contatti",
    title: "Contatti | Impresa Edile Lucca — Edilcomplex2020, Capannori (LU)",
    description:
      "Contatta Edilcomplex2020: Via delle Piastre 13/15, Guamo, Capannori (LU). Tel e WhatsApp +39 320 890 8036. Preventivi gratuiti per costruzioni e ristrutturazioni in Toscana.",
  },
  "/privacy": {
    name: "Privacy Policy",
    title: "Privacy Policy | Edilcomplex2020 S.r.l.",
    description:
      "Informativa sulla privacy di Edilcomplex2020 S.r.l.: come trattiamo i tuoi dati personali ai sensi del GDPR (Reg. UE 2016/679).",
  },
  "/cookie-policy": {
    name: "Cookie Policy",
    title: "Cookie Policy | Edilcomplex2020 S.r.l.",
    description:
      "Cookie policy del sito Edilcomplex2020: cookie tecnici utilizzati, servizi di terze parti e gestione delle preferenze dal browser.",
  },
  "/termini": {
    name: "Termini e Condizioni",
    title: "Termini e Condizioni | Edilcomplex2020 S.r.l.",
    description:
      "Termini e condizioni d'uso del sito web di Edilcomplex2020 S.r.l., impresa edile a Lucca, Capannori (LU).",
  },
};

const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: [
    "Costruzioni Residenziali",
    "Ristrutturazioni Edilizie",
    "Restauri e Risanamenti",
    "Progettazione e Costruzione Piscine",
    "Strutture Commerciali e Industriali",
    "Gestione e Coordinamento Progetti",
  ].map((name, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      name,
      provider: { "@id": `${BASE_URL}/#azienda` },
      areaServed: "Toscana",
    },
  })),
};

export default function SEO() {
  const { pathname } = useLocation();
  const meta = META[pathname] || META["/"];
  const canonical = `${BASE_URL}${pathname === "/" ? "" : pathname}`;

  useEffect(() => {
    pageView(pathname);
  }, [pathname]);

  const breadcrumb =
    pathname !== "/"
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
            { "@type": "ListItem", position: 2, name: meta.name, item: canonical },
          ],
        }
      : null;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="it" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {breadcrumb && <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>}
      {pathname === "/" && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.items.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      )}
      {pathname === "/servizi" && (
        <script type="application/ld+json">{JSON.stringify(SERVICES_SCHEMA)}</script>
      )}
    </Helmet>
  );
}
